import moment from 'moment';
import currency from '@/filters/currency';

export const PREFERENCES_KEY = 'financialOverview';
export const REGIMES = { competence: 'competence', cash: 'cash' };
export const HIDDEN_MONEY = 'R$ •••••';
export const TRANSACTIONS_PATH = '/financial/transactions';
export const STATEMENTS_PATH = '/financial/statements';

export function money(value, hidden) {
  if (hidden) {
    return HIDDEN_MONEY;
  }
  const number = Number(value || 0);
  const formatted = currency(Math.abs(number));
  return number < 0 ? `−${formatted}` : formatted;
}

export function percent(value) {
  if (value === null || value === undefined) {
    return '—';
  }
  return `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

export function compact(value, hidden) {
  if (hidden) {
    return '•••';
  }
  const number = Number(value || 0);
  const abs = Math.abs(number);
  const sign = number < 0 ? '−' : '';
  if (abs >= 1000000) {
    return `${sign}${(abs / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  }
  if (abs >= 1000) {
    return `${sign}${(abs / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`;
  }
  return `${sign}${abs.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
}

export function niceRange(low, high, maxSteps = 6) {
  const below = Math.abs(Math.min(0, Number(low || 0)));
  const above = Math.max(0, Number(high || 0));
  if (below + above === 0) {
    return { min: 0, max: 1, step: 1 };
  }
  const magnitude = 10 ** Math.floor(Math.log10((below + above) / maxSteps));
  const steps = size => Math.ceil(above / size) + Math.ceil(below / size);
  const factor = [1, 2, 2.5, 5, 10, 20].find(item => steps(item * magnitude) <= maxSteps);
  const step = factor * magnitude;
  return {
    min: -Math.ceil(below / step) * step,
    max: Math.ceil(above / step) * step,
    step,
  };
}

export function ticks({ min, max, step }) {
  const count = Math.round((max - min) / step);
  return Array.from({ length: count + 1 }, (_, index) => min + index * step);
}

export function shortDate(value) {
  return value ? moment(value, 'YYYY-MM-DD').format('DD/MM') : '';
}

export function longDate(value) {
  return moment(value, 'YYYY-MM-DD').format('dddd, D [de] MMMM');
}

export function monthLabel(month) {
  return moment(month, 'YYYY-MM').format('MMM/YY').toLowerCase();
}

export function monthName(month) {
  return moment(month, 'YYYY-MM').format('MMMM [de] YYYY');
}

export function dueLabel(dueDate, today) {
  if (dueDate < today) {
    const days = moment(today, 'YYYY-MM-DD').diff(moment(dueDate, 'YYYY-MM-DD'), 'days');
    return { text: days === 1 ? 'venceu ontem' : `vencida há ${days} dias`, tone: 'late' };
  }
  if (dueDate === today) {
    return { text: 'vence hoje', tone: 'today' };
  }
  const days = moment(dueDate, 'YYYY-MM-DD').diff(moment(today, 'YYYY-MM-DD'), 'days');
  return { text: days === 1 ? 'amanhã' : `em ${days} dias`, tone: 'future' };
}

export function transactionsRoute(query) {
  return {
    path: TRANSACTIONS_PATH,
    query: {
      status: 'open',
      dateType: 'dueDate',
      onlyDebt: 'true',
      ...query,
    },
  };
}

export function blankOverview() {
  return {
    date: moment().format('YYYY-MM-DD'),
    attention: {
      overdueReceivable: 0,
      overduePayable: 0,
      negativeAccounts: [],
      staleStatements: [],
    },
    today: {
      receivable: 0, payable: 0, received: 0, paid: 0, result: 0,
    },
    dueToday: { total: 0, items: [] },
    accounts: { items: [], total: 0 },
    cardReceivables: { value: 0, days: 30 },
    upcoming: [],
    coverage: {
      available: 0, revenue: 0, expense: 0, ratio: null,
    },
  };
}

export function blankPeriod() {
  return {
    result: {
      revenue: 0,
      expense: 0,
      value: 0,
      margin: null,
      previous: { value: 0, variation: null },
    },
    expenses: { total: 0, categories: [], entities: [] },
    receivables: { count: 0, value: 0, items: [] },
    delinquency: null,
    ticket: { revenue: 0, attendances: 0, value: null },
  };
}
